// =============================================================================
// features/relatorios/utils/format.ts — Formatação de taxas dos relatórios.
//
// Existe porque `fmtPercent` estava duplicado em 6 seções com a MESMA
// implementação errada: multiplicava por 100 um valor que a API já entrega em
// pontos percentuais. Em produção isso virou "10.000,0% de sucesso" no outbox e
// "201,0%" de análises sobre simulações (o valor real era 2,0%).
//
// O custo do bug não é estético: um número absurdo faz o cliente duvidar de
// todos os outros números da tela, inclusive dos corretos.
//
// A correção NÃO é remover o `* 100` de todo lugar — as duas unidades convivem:
//
//   - A API calcula com `Math.round(x * 10000) / 100`, devolvendo PONTOS
//     PERCENTUAIS (100 significa 100%). Ex: successRate, approvalRate,
//     handoffRate, conversionRate, defaultRate, sendRate.
//
//   - Alguns componentes derivam razões localmente (`refused / analyses`),
//     que são FRAÇÕES 0–1 e ainda precisam do fator.
//
// Por isso são duas funções com nome explícito, em vez de uma `fmtPercent`
// ambígua: o nome obriga quem escreve a saber de onde o número veio.
// =============================================================================

const PT_BR_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
};

/**
 * Formata um valor que JÁ está em pontos percentuais.
 *
 * Use para tudo que vem da API de relatórios.
 *
 * @example fmtPercentFromPoints(100)  // "100,0%"
 * @example fmtPercentFromPoints(2.01) // "2,0%"
 */
export function fmtPercentFromPoints(n: number): string {
  return n.toLocaleString('pt-BR', PT_BR_OPTIONS) + '%';
}

/**
 * Formata uma fração 0–1 como percentual.
 *
 * Use apenas para razões calculadas no próprio componente.
 *
 * @example fmtPercentFromRatio(0.25) // "25,0%"
 */
export function fmtPercentFromRatio(n: number): string {
  return (n * 100).toLocaleString('pt-BR', PT_BR_OPTIONS) + '%';
}

/**
 * Complemento de uma taxa em pontos percentuais.
 *
 * Existe para evitar a armadilha do `1 - taxa`, que estava em produção na seção
 * de IA: `handoffRate` vem em pontos (25 = 25%), então `1 - 25` dava -24, e a
 * tela mostrava "concluídas sem handoff: -2.400%". O complemento de pontos
 * percentuais é `100 - taxa`, não `1 - taxa`.
 *
 * @example complementOfPoints(25) // 75
 */
export function complementOfPoints(n: number): number {
  return 100 - n;
}
